import AddIcon from "@mui/icons-material/Add";
import AddCommentIcon from "@mui/icons-material/AddComment";
import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualsByIds,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import TeamInvitation from "marketplace/teams/invitations/team-invitation";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction, SetURLSearchParams } from "react-router";
import InvitationMessagePopover from "./invitation-message-popover";
import TeamMemberSelection from "./team-member-selection";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const TeamList = styled(List)(({ theme }) => ({
  margin: 0,
  marginTop: 0,
  minWidth: theme.spacing(40),
  paddingBottom: theme.spacing(3),
}));
const TeamListSubheader = styled(ListSubheader)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  display: "flex",
  fontSize: "1.3em",
  justifyContent: "space-between",
  padding: 0,
  "& > button": {
    height: "fit-content",
    width: "fit-content",
  },
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.3em",
  paddingTop: theme.spacing(1),
}));
const TeamMemberLink = styled(Link)(() => ({
  cursor: "pointer",
}));
const RemoveIcon = styled(DeleteIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));

type EditTeamProps = {
  className?: string;
  team: Team;
  invitations?: TeamInvitation[];
  removedUserIds?: Guid[];
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  onLeaderReplaced?: (leader: Individual, promoted: boolean) => void;
  onMemberAdded: (member: Individual) => void;
  onInvitationUpdated: (member: TeamInvitation) => void;
  onMemberRemoved: (individual: Individual) => void;
};

export default function EditTeam(props: Readonly<EditTeamProps>) {
  const {
    className,
    team,
    invitations,
    removedUserIds,
    navigate,
    searchParams,
    setSearchParams,
    onLeaderReplaced,
    onMemberAdded,
    onInvitationUpdated,
    onMemberRemoved,
  } = props;

  const [currentMemberEditing, setCurrentMemberEditing] = useState<TeamInvitation>();
  const [selectedLeader, setSelectedLeader] = useState<Individual>(team.leader);
  const [selectedMembers, setSelectedMembers] = useState<Individual[]>(team.members);
  const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([]);
  const [popoverAnchorElement, setPopoverAnchorElement] = useState<HTMLElement | null>(null);

  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();

  const invitedIndividualIds: Guid[] = useMemo(() => {
    return invitations?.map((invite) => invite.inviteeId) ?? [];
  }, [invitations]);

  const invitedIndividuals: Individual[] = useSelector(getIndividualsByIds(invitedIndividualIds));
  useGetIndividualsByIdsQuery(
    { ids: invitedIndividualIds.map((id) => id.value) },
    { skip: !invitedIndividualIds.length || !!invitedIndividuals.length },
  );

  function getSelectedTeamMemberIds(): Guid[] {
    const userIds: Guid[] = selectedMembers?.map((member) => member.userId) ?? [];
    if (selectedLeader?.userId) userIds.push(selectedLeader.userId);
    userIds.push(...invitedIndividuals.map((individual) => individual.userId));
    return userIds;
  }

  /**
   * Handles what happens when add leader is clicked
   */
  function beginLeaderSelection() {
    openDialog({
      title: "Select Leader",
      component: (
        <TeamMemberSelection
          navigate={navigate}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          onNewSelection={handleNewLeaderSelected}
          selectLeader={true}
          selectMember={false}
          selectedTeamMemberUserIds={getSelectedTeamMemberIds()}
          popDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  /**
   * Handles when a new leader has been selected
   *
   * @param leader The new leader to replace the current leader with
   */
  async function handleNewLeaderSelected(leader: Individual) {
    if (selectedLeader) {
      const response = await confirm({
        title: "Replace Leader?",
        message: `Do you want to replace the current team leader with ${leader.profile?.firstName} ${leader.profile?.lastName}?`,
        okButtonText: "Replace",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    setSelectedLeader(leader);

    if (selectedMembers?.find((member) => member.isEqualTo(leader))) {
      setSelectedMembers(selectedMembers?.filter((member) => !member.isEqualTo(leader)));
      if (onLeaderReplaced) onLeaderReplaced(leader, false);
      return;
    }
    if (onLeaderReplaced) {
      onLeaderReplaced(leader, false);
    }
  }

  /**
   * Handles what happens when add member is clicked
   */
  function beginMemberSelection() {
    openDialog({
      title: "Select Member",
      component: (
        <TeamMemberSelection
          navigate={navigate}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          onNewSelection={handleNewMemberSelected}
          selectLeader={false}
          selectMember={true}
          selectedTeamMemberUserIds={getSelectedTeamMemberIds()}
          popDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }

  /**
   * Handles when a new member has been selected
   *
   * @param newMember The new member to add to the vendor-team
   */
  function handleNewMemberSelected(newMember: Individual) {
    if (selectedMembers?.some((member) => member.isEqualTo(newMember))) {
      enqueueSnackbar("Selected user is already on vendor-team", {
        variant: "info",
        preventDuplicate: true,
        autoHideDuration: 7500,
      });
      return;
    }

    setSelectedMembers([...(selectedMembers ?? []), newMember]);
    onMemberAdded(newMember);
  }

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   * @param individual The individual to remove
   */
  async function handleMemberRemoved(individual?: Individual) {
    if (!individual) return;

    const response = await confirm({
      title: "Remove Member?",
      message: `Do you want to remove ${individual.profile?.fullName} from the team?`,
      okButtonText: "Remove",
    });

    if (response === ConfirmResponse.Cancel) return;

    const newMembers = selectedMembers?.filter((member) => !member.isEqualTo(individual));
    setSelectedMembers(newMembers);
    onMemberRemoved(individual);
  }

  /**
   * Handles what happens when the add/edit message popover is opened
   *
   * @param event The event that triggered the popover to open
   * @param member The member to edit the message for
   */
  function handleMessagePopoverOpened(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, member?: TeamInvitation) {
    setCurrentMemberEditing(member);
    setPopoverAnchorElement(event?.currentTarget);
  }

  /**
   * Handles what happens when a vendor-team member's invitation message is changed
   *
   * @param userId The userId of the member whose message was changed
   * @param message The new message
   */
  function handleInvitationMessageChanged(userId?: Guid, message?: string) {
    setPopoverAnchorElement(null);

    if (!userId || !invitations) return;

    const userHasPendingInvite: boolean = !!invitations?.find((invitation) => invitation.inviteeId?.isEqualTo(userId));

    if (!userHasPendingInvite) return;

    let targetInvitation: TeamInvitation | undefined;

    const updatedInvitations: TeamInvitation[] = invitations.map((invitation) => {
      if (invitation.inviteeId?.isEqualTo(userId)) {
        const updatedInvite = {
          ...invitation,
          message: message,
        } as TeamInvitation;
        targetInvitation = updatedInvite;
        return updatedInvite;
      }
      return invitation;
    });

    if (!targetInvitation) return;

    setPendingInvitations(updatedInvitations);
    onInvitationUpdated(targetInvitation);
  }

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  function openUserProfileDialog(individualId: Guid) {
    openDialog({
      component: <ViewIndividualProfile individualId={individualId} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  /**
   * Renders a single individual's info as a list item
   *
   * @param userId The individual's info to render
   * @param allowDelete Whether or not to allow the user to be removed from the vendor-team
   */
  function renderIndividual(individual: Individual, allowDelete?: boolean) {
    const isRemoved: boolean = removedUserIds?.some((id) => id.isEqualTo(individual.userId)) ?? false;

    return (
      <ListItem
        key={individual.id?.value}
        disableGutters
        secondaryAction={
          <>
            {!isRemoved && allowDelete && (
              <Tooltip title="Remove Member">
                <span>
                  <IconButton edge="end" onClick={() => handleMemberRemoved(individual)} size="medium">
                    <RemoveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </>
        }
      >
        <ListItemAvatar>
          <IndividualAvatar individual={individual} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography>
              <TeamMemberLink onClick={() => openUserProfileDialog(individual.id)}>
                {individual.profile?.fullName}
              </TeamMemberLink>
            </Typography>
          }
          secondary={isRemoved ? "Removing From Team" : "Active Member"}
        />
      </ListItem>
    );
  }

  /**
   * Renders a single individual's invitation info as a list item
   */
  function renderIndividualInvitation(invitation: TeamInvitation) {
    const individual: Individual | undefined = invitedIndividuals.find((individual) =>
      individual.id.isEqualTo(invitation.inviteeId),
    );

    if (!individual) {
      return <ListItem>Error: Individual not found</ListItem>;
    }

    const isRemoved: boolean = removedUserIds?.some((id) => id.isEqualTo(individual.userId)) ?? false;

    return (
      <ListItem
        key={invitation.inviteeId.value}
        disableGutters
        secondaryAction={
          <>
            {!isRemoved && (
              <Tooltip title="Cancel Invitation">
                <span>
                  <IconButton edge="end" onClick={() => handleMemberRemoved(individual)} size="medium">
                    <RemoveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </>
        }
      >
        <ListItemAvatar>
          <IndividualAvatar individual={individual} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography>
              <TeamMemberLink onClick={() => openUserProfileDialog(invitation.id)}>
                {individual?.profile?.fullName}
              </TeamMemberLink>
            </Typography>
          }
          secondary={isRemoved ? "Revoking Invitation" : "Invited"}
        />
      </ListItem>
    );
  }

  /**
   * Renders a single vendor-team member invite as a list item
   *
   * @param invitation The invitation to render
   * @returns
   */
  function renderTeamInvitation(invitation: TeamInvitation) {
    const individual: Individual | undefined = invitedIndividuals.find((individual) =>
      individual.id.isEqualTo(invitation.inviteeId),
    );

    if (!individual) {
      return <ListItem>Error: Individual not found</ListItem>;
    }

    const isRemoved: boolean = removedUserIds?.some(
      (id) => id.isEqualTo(individual.userId)) ?? false;
    const isUnsent: boolean = pendingInvitations?.some(
      (pendingInvite) => pendingInvite.id?.isEqualTo(invitation.id)) ?? false;
      
    return (
      <ListItem
        key={invitation.inviteeId?.value}
        disableGutters
        secondaryAction={
          <>
            {!isRemoved && (
              <>
                <Tooltip title={`${invitation.message ? "Edit" : "Add"} Custom Invitation Message`}>
                  <IconButton size="medium" onClick={(event) => handleMessagePopoverOpened(event, invitation)}>
                    {invitation.message ? <CommentIcon color="secondary" /> : <AddCommentIcon color="secondary" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove Invitation">
                  <span>
                    <IconButton edge="end" onClick={() => handleMemberRemoved(individual)} size="medium">
                      <RemoveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </>
        }
      >
        <ListItemAvatar>
          {individual?.id ? <IndividualAvatar individual={individual} /> : <Avatar />}
        </ListItemAvatar>
        {(() => {
          let secondaryText: string;
          if (isRemoved) {
            secondaryText = "Revoking Invitation";
          } else if (isUnsent) {
            secondaryText = "Pending Invitation";
          } else {
            secondaryText = "Invited";
          }
          return (
            <ListItemText
              primary={
                <Typography>
                  <TeamMemberLink onClick={() => openUserProfileDialog(invitation.id)}>
                    {individual?.profile?.fullName}
                  </TeamMemberLink>
                </Typography>
              }
              secondary={secondaryText}
            />
          );
        })()}
      </ListItem>
    );
  }

  return (
    <MainContainer className={className}>
      <TeamContainer>
        <TeamList>
          <TeamListSubheader>
            <span>Team Leader</span>
            <Button
              color="primary"
              startIcon={<AddIcon />}
              size="small"
              sx={{ paddingRight: 0 }}
              disabled={!onLeaderReplaced}
              onClick={beginLeaderSelection}
            >
              Select Leader
            </Button>
          </TeamListSubheader>
          {!selectedLeader && (
            <ListItem disableGutters>
              <NoRowsPlaceholder>None</NoRowsPlaceholder>
            </ListItem>
          )}
          {selectedLeader && renderIndividual(selectedLeader)}
        </TeamList>
        <TeamList>
          <TeamListSubheader>
            <span>Team Members</span>
            <Button
              color="primary"
              startIcon={<AddIcon />}
              size="small"
              sx={{ paddingRight: 0 }}
              disabled={!selectedLeader}
              onClick={beginMemberSelection}
            >
              Add Member
            </Button>
          </TeamListSubheader>
          {(!selectedMembers || selectedMembers.length < 1) &&
            (!invitations || invitations.length < 1) &&
            (!pendingInvitations || pendingInvitations.length < 1) && (
              <ListItem disableGutters>
                <NoRowsPlaceholder>None</NoRowsPlaceholder>
              </ListItem>
            )}
          {selectedMembers?.map((member) => renderIndividual(member, true))}
          {invitations?.map((member: TeamInvitation) => renderTeamInvitation(member))}
          {pendingInvitations?.map((invitation: TeamInvitation) => renderIndividualInvitation(invitation))}
        </TeamList>
      </TeamContainer>
      <InvitationMessagePopover
        userId={currentMemberEditing?.inviteeId ?? undefined}
        anchorEl={popoverAnchorElement}
        message={currentMemberEditing?.message}
        onCanceled={() => setPopoverAnchorElement(null)}
        onMessageChanged={(userId?: Guid, message?: string) => {
          handleInvitationMessageChanged(userId, message);
        }}
      />
    </MainContainer>
  );
}
